import { requiredEnv } from './requiredEnv';
import { logger } from './logger';

/**
 * "Send a Velvynote" API client.
 * Lets a logged-in user privately send a note to someone by email, and lets a
 * recipient open the note via its one-time token.
 */

const BACKEND_URL = requiredEnv('REACT_APP_BACKEND_URL');

/**
 * Send an owned note to one or more recipients by email.
 * @param {Object} user - Firebase user (for auth token)
 * @param {string} memoryId - The note's UUID
 * @param {Object} opts
 * @param {string[]} opts.recipients - Recipient email addresses (max 3)
 * @param {string} [opts.coverMessage] - Optional short note shown before the reveal
 * @param {boolean} [opts.anonymous] - Hide identity everywhere
 * @param {boolean} [opts.revealOnOpen] - Hide identity in the email, reveal after unfold
 */
export async function sendNoteByEmail(user, memoryId, { recipients, coverMessage, anonymous, revealOnOpen }) {
  if (!user) return { success: false, error: 'You must be signed in to send a note' };

  try {
    const idToken = await user.getIdToken();
    const response = await fetch(`${BACKEND_URL}/memories/${memoryId}/send`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        recipients,
        coverMessage: coverMessage || null,
        anonymous: !!anonymous,
        revealOnOpen: !!revealOnOpen,
      }),
    });

    const data = await response.json();
    if (!response.ok) {
      return { success: false, error: data.error || `Failed to send note: ${response.status}` };
    }
    return data;
  } catch (err) {
    logger.error('Failed to send note by email:', err);
    return { success: false, error: err.message };
  }
}

/**
 * Open a received note by its token (public, no auth).
 * @param {string} token
 */
export async function getReceivedNote(token) {
  try {
    const response = await fetch(`${BACKEND_URL}/note-sends/${token}`);
    const data = await response.json();
    if (!response.ok) {
      return { success: false, error: data.error || 'This note is no longer available' };
    }
    return data;
  } catch (err) {
    logger.error('Failed to fetch received note:', err);
    return { success: false, error: err.message };
  }
}

/**
 * Recipient opt-out: stop receiving Velvynotes at this address.
 * @param {string} token
 */
export async function blockNoteSender(token) {
  try {
    const response = await fetch(`${BACKEND_URL}/note-sends/${token}/block`, { method: 'POST' });
    const data = await response.json();
    if (!response.ok) {
      return { success: false, error: data.error || 'Failed to update preferences' };
    }
    return data;
  } catch (err) {
    logger.error('Failed to block sender:', err);
    return { success: false, error: err.message };
  }
}
